import { useApolloClient } from "@apollo/client/react";
import { map } from "lodash";
import { useCallback, useMemo, useState } from "react";

import {
  peopleSearch,
  getBasePeopleByIds,
  peopleSearchWithReportingLine,
  getBasePeopleWithReportingLineByIds,
  PersonSearchBaseFragment,
} from "src/queries/person.graphql";
import { useDebouncedPersonSearch } from "src/hooks/useDebouncedPersonSearch";
import { fullDisplayName } from "src/util/personName";

export const createPersonOptions = (people) => {
  return map(people, (person) => {
    return {
      label: fullDisplayName(person),
      value: person.aggregateId,
      person,
    };
  });
};

export const useSearchPerson = ({
  fetchPolicy = "no-cache",
  withReportingLine = false,
  includeSupplyReportingLine = false,
} = {}) => {
  const extraVariables = useMemo(
    () => (withReportingLine ? { includeSupplyReportingLine } : undefined),
    [withReportingLine, includeSupplyReportingLine]
  );
  return useDebouncedPersonSearch({
    query: withReportingLine ? peopleSearchWithReportingLine : peopleSearch,
    mapPeopleToOptions: createPersonOptions,
    fetchPolicy,
    extraVariables,
  });
};

export const useLoadPeople = ({
  withReportingLine = false,
  includeSupplyReportingLine = false,
} = {}) => {
  const client = useApolloClient();
  const [isLoading, setIsLoading] = useState(false);

  const loadPeople = useCallback(
    async (ids) => {
      setIsLoading(true);
      const {
        data: { people },
      } = await client.query({
        query: withReportingLine
          ? getBasePeopleWithReportingLineByIds
          : getBasePeopleByIds,
        variables: {
          ids,
          ...(withReportingLine && { includeSupplyReportingLine }),
        },
      });

      setIsLoading(false);
      return people;
    },
    [client, withReportingLine, includeSupplyReportingLine]
  );

  return {
    loadPeople,
    isLoading,
    client,
  };
};

export const useCachedPerson = () => {
  const client = useApolloClient();

  const getCachedPerson = useCallback(
    (aggregateId) => {
      const person = client.readFragment({
        id: `Person:${aggregateId}`,
        fragment: PersonSearchBaseFragment,
        fragmentName: "PersonSearchBaseFragment",
      });

      return person;
    },
    [client]
  );

  return {
    getCachedPerson,
    client,
  };
};
